import React, { useEffect } from 'react';
import { useAuth } from '@shared/components/AuthProvider';
import { useHistory } from '@shared/core';
import { useRouterHelper } from '@shared/core/router.helper';
import { useQueryParamHelper } from '@shared/core/queryString';
import { routePaths } from '@apps/acquisitionFunnels/createWedding/route.paths';
import type { CreateWeddingGuardsProps } from '@apps/acquisitionFunnels/createWedding/CreateWedding.types';

/**
 * Combined auth guard for CreateWedding routes.
 *
 * Renders `children` immediately; auth-driven redirects fire post-hydration via useEffect.
 * The SSR'd HTML is a public view safe to cache; auth-aware UI inside `children` is gated
 * separately on `isLoggedIn` at the consumer level (see CreateWeddingForm sign-out affordance).
 *
 * - authMode="allowOnlyAuthenticated": redirects to /create-account if not logged in
 * - authMode="allowOnlyUnauthenticated": redirects to /createwedding if logged in
 */
export const CreateWeddingGuards: React.FC<CreateWeddingGuardsProps> = ({ children, authMode }) => {
  const { isCurrentUserSettled, isLoggedIn } = useAuth();
  const history = useHistory();
  const routeHelpers = useRouterHelper();
  const queryParamHelper = useQueryParamHelper();

  const isReady = isCurrentUserSettled;

  const shouldRedirectToCreateAccount = authMode === 'allowOnlyAuthenticated' && !isLoggedIn;

  const shouldRedirectToCreateWedding = authMode === 'allowOnlyUnauthenticated' && isLoggedIn;

  useEffect(() => {
    if (!isReady) {
      return;
    }

    switch (authMode) {
      case 'allowOnlyAuthenticated': {
        if (shouldRedirectToCreateAccount) {
          const path = queryParamHelper.buildRedirectPath(routeHelpers.buildPath(routePaths.createAccount.path));
          history.replace(path);
        }
        break;
      }

      case 'allowOnlyUnauthenticated': {
        if (shouldRedirectToCreateWedding) {
          const path = queryParamHelper.buildRedirectPath(routeHelpers.buildPath(routePaths.createWeddingForm.path));
          history.replace(path);
        }
        break;
      }

      default:
        break;
    }
  }, [authMode, isReady, shouldRedirectToCreateAccount, shouldRedirectToCreateWedding, history, routeHelpers, queryParamHelper]);

  return <>{children}</>;
};
